import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { IconEye, IconLock, IconEyeOff } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { useFormatText } from '@/hooks/useFormatText';
import {
  changePasswordSchema,
  type TChangePassword,
} from '@/schema/changePasswordSchema';
import { changePassword } from '@/service/settingsService';
import { encryptPassword } from '@/utils/encryption';
import { zodResolver } from '@hookform/resolvers/zod';

const AccountSecurity = () => {
  const { formatText } = useFormatText();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TChangePassword>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: TChangePassword) => {
    setLoading(true);
    try {
      const response = await changePassword({
        current_password: encryptPassword(data?.currentPassword),
        new_password: encryptPassword(data?.newPassword),
      });
      if (response?.status) {
        enqueueSnackbar(response?.message, {
          variant: 'success',
        });
        reset();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-216 w-full" data-testid="account-security-container">
      {/* Section Title */}
      <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
        {formatText('settings.account-security.title')}
      </h2>

      {/* Card */}
      <div className="mt-6 border border-neutral-light-200 rounded-xl">
        <div className="p-6">
          <h3 className="text-base font-semibold text-neutral-primary">
            {formatText('settings.account-security.change-password')}
          </h3>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-6 flex flex-col gap-3"
          >
            <Input
              name="currentPassword"
              label={formatText('settings.account-security.current-password')}
              type={showCurrentPassword ? 'text' : 'password'}
              register={register}
              required
              error={
                errors.currentPassword?.message
                  ? formatText(errors.currentPassword.message)
                  : undefined
              }
              leftIcon={{
                icon: <IconLock size={20} className="text-neutral-secondary" />,
              }}
              rightIcon={{
                icon: showCurrentPassword ? (
                  <IconEyeOff size={20} className="text-neutral-secondary" />
                ) : (
                  <IconEye size={20} className="text-neutral-secondary" />
                ),
                onClick: () => setShowCurrentPassword((prev) => !prev),
              }}
              placeholder="••••••••••••"
            />

            <Input
              name="newPassword"
              label={formatText('settings.account-security.new-password')}
              type={showNewPassword ? 'text' : 'password'}
              register={register}
              required
              error={
                errors.newPassword?.message
                  ? formatText(errors.newPassword.message)
                  : undefined
              }
              leftIcon={{
                icon: <IconLock size={20} className="text-neutral-secondary" />,
              }}
              rightIcon={{
                icon: showNewPassword ? (
                  <IconEyeOff size={20} className="text-neutral-secondary" />
                ) : (
                  <IconEye size={20} className="text-neutral-secondary" />
                ),
                onClick: () => setShowNewPassword((prev) => !prev),
              }}
              placeholder="••••••••••••"
            />

            <Input
              name="confirmNewPassword"
              label={formatText(
                'settings.account-security.confirm-new-password'
              )}
              type={showConfirmPassword ? 'text' : 'password'}
              register={register}
              required
              error={
                errors.confirmNewPassword?.message
                  ? formatText(errors.confirmNewPassword.message)
                  : undefined
              }
              leftIcon={{
                icon: <IconLock size={20} className="text-neutral-secondary" />,
              }}
              rightIcon={{
                icon: showConfirmPassword ? (
                  <IconEyeOff size={20} className="text-neutral-secondary" />
                ) : (
                  <IconEye size={20} className="text-neutral-secondary" />
                ),
                onClick: () => setShowConfirmPassword((prev) => !prev),
              }}
              placeholder="••••••••••••"
            />
          </form>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <Button
          label={formatText('settings.account-security.save-password')}
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default AccountSecurity;
